import DataObjectIcon from "@mui/icons-material/DataObject";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import {Divider, Popover, Stack} from "@mui/material";
import {useEditorState, type Editor} from "@tiptap/react";
import {useMemo, useState, type MouseEvent} from "react";
import {useTranslation} from "react-i18next";
import EmojiPicker from "../../emojiPicker/EmojiPicker";
import {pickerItemsFor, VariablePickerPopover} from "../../newVariablePicker";
import {useVariableCatalogue} from "../../newVariablePicker/chip";
import {insertVariable} from "../../newVariablePicker/editor";
import {FLOATING_SHADOW} from "../../newVariablePicker/shared/floatingShadow";
import LinkPopover from "./LinkPopover";
import ToolbarButton from "./ToolbarButton";
import ToolbarSelect from "./ToolbarSelect";

interface Props {
    editor: Editor;
    lists: boolean;
    alignment: boolean;
    emoji: boolean;
}

type Popup = 'link' | 'emoji' | 'variable';
type Alignment = 'left' | 'center' | 'right';

const GroupDivider = () => <Divider orientation={'vertical'} flexItem sx={{mx: 0.5, my: 0.75}}/>;

/*
    The emoji picker draws its own outlined surface; the Popover's paper only positions it and adds
    the shadow every floating surface here has.
 */
const EMOJI_POPOVER_PAPER_SX = {
    mt: '8px',
    borderRadius: 2,
    bgcolor: 'transparent',
    backgroundImage: 'none',
    boxShadow: FLOATING_SHADOW,
};

/** The buttons follow the field: lists only on several lines, the rest only when the host switches it on. */
export default function RichTextToolbar({editor, lists, alignment, emoji}: Props) {
    const {t} = useTranslation();
    const [popup, setPopup] = useState<{kind: Popup; anchor: HTMLElement} | null>(null);

    // The field's own catalogue, so the button and the `/` menu always offer the same variables.
    const catalogue = useVariableCatalogue(editor);
    const variableItems = useMemo(
        () => pickerItemsFor(catalogue.variables, catalogue.preview),
        [catalogue],
    );

    const state = useEditorState({
        editor,
        selector: ({editor: instance}) => ({
            bold: instance.isActive('bold'),
            italic: instance.isActive('italic'),
            underline: instance.isActive('underline'),
            bulletList: instance.isActive('bulletList'),
            orderedList: instance.isActive('orderedList'),
            link: instance.isActive('link'),
            alignment: (instance.isActive({textAlign: 'center'}) ? 'center'
                : instance.isActive({textAlign: 'right'}) ? 'right' : 'left') as Alignment,
            canUndo: instance.can().undo(),
            canRedo: instance.can().redo(),
        }),
    });

    const open = (kind: Popup) => (event: MouseEvent<HTMLButtonElement>) =>
        setPopup({kind, anchor: event.currentTarget});
    const close = () => setPopup(null);
    const isOpen = (kind: Popup) => popup?.kind === kind;

    const applyLink = (href: string) => {
        close();
        if (editor.state.selection.empty && !editor.isActive('link')) {
            editor.chain().focus().insertContent({type: 'text', text: href, marks: [{type: 'link', attrs: {href}}]}).run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({href}).run();
    };

    const removeLink = () => {
        close();
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
    };

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            flexWrap={'wrap'}
            gap={0.25}
            role={'toolbar'}
            aria-label={t('Formatting')}
            // A click between the buttons leaves the text focused as well. Only for this element's own DOM:
            // React also bubbles mousedowns from the portaled popups up to here, and those must reach their inputs.
            onMouseDown={event => {
                if (event.currentTarget.contains(event.target as Node)) event.preventDefault();
            }}
            sx={{px: 0.75, py: 0.5, borderBottom: '1px solid', borderColor: 'divider'}}
        >
            <ToolbarButton label={t('Bold')} icon={<FormatBoldIcon fontSize={'small'}/>} active={state.bold}
                           onClick={() => editor.chain().focus().toggleBold().run()}/>
            <ToolbarButton label={t('Italic')} icon={<FormatItalicIcon fontSize={'small'}/>} active={state.italic}
                           onClick={() => editor.chain().focus().toggleItalic().run()}/>
            <ToolbarButton label={t('Underline')} icon={<FormatUnderlinedIcon fontSize={'small'}/>}
                           active={state.underline}
                           onClick={() => editor.chain().focus().toggleUnderline().run()}/>

            {lists && (
                <>
                    <GroupDivider/>
                    <ToolbarButton label={t('Bulleted list')} icon={<FormatListBulletedIcon fontSize={'small'}/>}
                                   active={state.bulletList}
                                   onClick={() => editor.chain().focus().toggleBulletList().run()}/>
                    <ToolbarButton label={t('Numbered list')} icon={<FormatListNumberedIcon fontSize={'small'}/>}
                                   active={state.orderedList}
                                   onClick={() => editor.chain().focus().toggleOrderedList().run()}/>
                </>
            )}

            {alignment && (
                <>
                    <GroupDivider/>
                    <ToolbarSelect<Alignment>
                        label={t('Alignment')}
                        value={state.alignment}
                        options={[
                            {value: 'left', label: t('Align left'), icon: <FormatAlignLeftIcon fontSize={'small'}/>},
                            {value: 'center', label: t('Align center'), icon: <FormatAlignCenterIcon fontSize={'small'}/>},
                            {value: 'right', label: t('Align right'), icon: <FormatAlignRightIcon fontSize={'small'}/>},
                        ]}
                        // No alignment is left: choosing left removes it, so the text stays plain markdown.
                        onChange={value => value === 'left'
                            ? editor.chain().focus().unsetTextAlign().run()
                            : editor.chain().focus().setTextAlign(value).run()}
                    />
                </>
            )}

            <GroupDivider/>
            <ToolbarButton label={t('Link')} icon={<InsertLinkIcon fontSize={'small'}/>}
                           active={state.link || isOpen('link')} onClick={open('link')}/>

            {(emoji || !!variableItems.length) && <GroupDivider/>}
            {emoji && (
                <ToolbarButton label={t('Insert emoji')} icon={<EmojiEmotionsOutlinedIcon fontSize={'small'}/>}
                               active={isOpen('emoji')} onClick={open('emoji')}/>
            )}
            {!!variableItems.length && (
                <ToolbarButton label={t('Insert variable')} icon={<DataObjectIcon fontSize={'small'}/>}
                               active={isOpen('variable')} onClick={open('variable')}/>
            )}

            {/* At the far end, as in the automation email toolbar. */}
            <Stack direction={'row'} gap={0.25} sx={{ml: 'auto'}}>
                <ToolbarButton label={t('Undo')} icon={<UndoIcon fontSize={'small'}/>} disabled={!state.canUndo}
                               onClick={() => editor.chain().focus().undo().run()}/>
                <ToolbarButton label={t('Redo')} icon={<RedoIcon fontSize={'small'}/>} disabled={!state.canRedo}
                               onClick={() => editor.chain().focus().redo().run()}/>
            </Stack>

            {popup?.kind === 'link' && (
                <LinkPopover
                    anchorEl={popup.anchor}
                    href={state.link ? (editor.getAttributes('link').href as string | undefined) : undefined}
                    onApply={applyLink}
                    onRemove={removeLink}
                    onClose={() => {
                        close();
                        editor.commands.focus();
                    }}
                />
            )}

            <Popover
                open={isOpen('emoji')}
                anchorEl={popup?.anchor}
                onClose={close}
                transitionDuration={0}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {sx: EMOJI_POPOVER_PAPER_SX}}}
            >
                <EmojiPicker
                    onSelect={selection => {
                        close();
                        editor.chain().focus().insertContent({type: 'text', text: selection.emoji}).run();
                    }}
                />
            </Popover>

            <VariablePickerPopover
                anchorEl={popup?.anchor ?? null}
                open={isOpen('variable') && !!variableItems.length}
                items={variableItems}
                onSelect={item => {
                    close();
                    insertVariable(editor, item.definition);
                }}
                onClose={close}
            />
        </Stack>
    );
}
